import { fetchRelatedPgProducts, fetchRelatedTgProducts } from '~/features/catalog/article.server';
import { optionalProtectedResourceLoader } from '~/shell/utils/authUtil.server';
import { getSelectedLocation } from '~/shell/utils/locationUtil.server';
import invariant from 'tiny-invariant';
import { useQuery } from '@tanstack/react-query';
import { useBasePath } from '~/common/hooks/useBasePath';
import { TgArea } from '~/common/types/area';

export const loader = optionalProtectedResourceLoader(async ({ params, request, loginInfo, t, api }) => {
  const isTgArea = params.area === TgArea;
  const articleNumber = params.articleNumber;

  invariant(articleNumber, 'Expected articleNumber');

  const actionExclude = loginInfo?.actionExclude ?? false;
  const hasIndividualOrderEndTimes = loginInfo?.hasIndividualOrderEndTimes ?? false;
  const ccId = await getSelectedLocation(request);

  const searchResponse = isTgArea
    ? await fetchRelatedTgProducts(api, request.signal, hasIndividualOrderEndTimes, articleNumber, t, actionExclude)
    : await fetchRelatedPgProducts(api, request.signal, articleNumber, ccId, t);

  const articles = searchResponse.articles.filter(article => article.articleNumber !== articleNumber);

  return Response.json({ articles });
});

export function useRelatedArticles(articleNumber: string, agrId: number) {
  const { path } = useBasePath();

  const fetchRelatedArticles = async () => {
    const response = await fetch(`${path}/resources/articles/${articleNumber}/relatedArticles/${agrId}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch related articles: ${response.statusText}`);
    }

    return response.json();
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['relatedArticles', articleNumber, agrId],
    queryFn: fetchRelatedArticles,
    enabled: !!articleNumber && !!agrId,
    staleTime: 5 * 60 * 1000
  });

  return {
    articles: data?.articles ?? [],
    isLoading,
    error
  };
}
